import * as Comlink from 'comlink'
import debug from 'debug'
import { computed, MaybeRefOrGetter, shallowRef, toValue, watch } from 'vue'

import type { AsyncWriteFile } from './opfs-handler'

const log = debug('quantistry:opfs')

function isFilePath(path?: string): path is string {
  return !path?.endsWith('/')
}

const INITIALIZE_SYMBOL = Symbol('OPFS')

export interface AbstractOPFS {
  writeFile(path: string, content: string | ArrayBuffer): Promise<void>
  writeJSONFile(path: string, content: Parameters<typeof JSON.stringify>[0]): Promise<void>

  readFile(path: string): Promise<File | undefined>
  readJSONFile(path: string): Promise<any>

  readDirectory(
    path: string
  ): Promise<AsyncIterableIterator<FileSystemDirectoryHandle | FileSystemFileHandle>>

  removeDirectory(path: string): void

  dispose(): void
}

let worker: Worker | undefined
const workerUsers = new Set<AbstractOPFS>()

/**
 * Interact with the browsers Origin Private File System.
 */
export class OPFS implements AbstractOPFS {
  #writeFile
  #encoder = new TextEncoder()
  #worker: Worker

  #opfsRoot: FileSystemDirectoryHandle
  #root: FileSystemDirectoryHandle

  /**
   * To initialize `OPFS` use `OPFS.initialize` instead of `new OPFS`.
   */
  constructor(
    root: FileSystemDirectoryHandle,
    opfsRoot: FileSystemDirectoryHandle,
    symbol: symbol
  ) {
    if (symbol !== INITIALIZE_SYMBOL)
      throw new TypeError('OPFS must be created with `OPFS.initialize`!')

    if (!worker) {
      worker = new Worker(new URL('./opfs-handler.ts', import.meta.url), {
        type: 'module',
      })
    }

    this.#worker = worker
    workerUsers.add(this)

    const asyncWriter = Comlink.wrap<typeof AsyncWriteFile>(this.#worker)
    this.#writeFile = asyncWriter['write']
    this.#root = root
    this.#opfsRoot = opfsRoot
  }

  /**
   * Initialize the OPFS by providing a root directory. All operations will be relative to this directory.
   */
  static async initialize(root: URL) {
    log(`initializing`)
    const opfsRoot = await navigator.storage.getDirectory()
    const directory = await opfsRoot.getDirectoryHandle(root.host, { create: true })

    const opfs = new OPFS(directory, opfsRoot, INITIALIZE_SYMBOL)
    log(`initialized`)
    return opfs
  }

  get root() {
    return this.#root
  }

  async #resolveDirectory(path: string, options?: FileSystemGetDirectoryOptions) {
    let directory = this.#root
    const normalizedPath = path.split('/')

    normalizedPath.pop()

    for (const segment of normalizedPath) {
      directory = await directory.getDirectoryHandle(segment, options)
    }

    return directory
  }

  /**
   * @example
   * await fs.writeFile('example.txt', 'Hello World!')
   */
  async writeFile(path: string, content: string | ArrayBuffer) {
    if (typeof content === 'string') {
      content = this.#encoder.encode(content).buffer as ArrayBuffer
    }

    const dir = await this.#opfsRoot.resolve(await this.#resolveDirectory(path, { create: true }))
    const filename = path.split('/').at(-1)

    if (dir && isFilePath(filename)) {
      log(`Write: ${filename}`, { dir })
      await this.#writeFile(dir, filename, Comlink.transfer(content, [content]))
      log(`Done: ${filename}`, { dir })
    }
  }

  /**
   * @example
   * await fs.writeFile('example.json', { message: 'Hello World!' })
   */
  async writeJSONFile(path: string, content: Parameters<typeof JSON.stringify>[0]) {
    const data = JSON.stringify(content)
    await this.writeFile(path, data)
  }

  /**
   * @example
   * const file = await fs.readFile('example.txt')
   */
  async readFile(path: string) {
    const dir = await this.#resolveDirectory(path)
    const filename = path.split('/').at(-1)

    if (isFilePath(filename)) {
      log(`Read: ${filename}`, { dir: dir.name })
      const fileHandle = await dir.getFileHandle(filename)
      const file = await fileHandle.getFile()
      log(`Done: ${filename}`, { dir: dir.name })
      return file
    }
  }

  /**
   * @example
   * const json = await fs.readJSONFile('example.json')
   */
  async readJSONFile(path: string) {
    const file = await this.readFile(path)

    if (!file) return

    return JSON.parse(await file.text())
  }

  /**
   * Returns all entries within the given directory.
   */
  async readDirectory(path: string) {
    const dir = await this.#resolveDirectory(path)
    return dir.values()
  }

  /**
   * Removes a directory.
   */
  async removeDirectory(path: string) {
    const dir = await this.#resolveDirectory(path)
    const directoryList = (await this.#root.resolve(dir)) ?? []
    directoryList?.pop()

    let directory = this.#root
    for (const segment of directoryList) {
      directory = await directory.getDirectoryHandle(segment)
    }

    await directory.removeEntry(dir.name, { recursive: true })
  }

  dispose() {
    workerUsers.delete(this)
    if (!workerUsers.size) {
      worker?.terminate()
      worker = undefined
    }
    log(`cleanup`)
  }
}

export class InMemoryOPFS implements AbstractOPFS {
  #fs = new Map<string, string | ArrayBuffer>()

  /**
   * @example
   * await fs.writeFile('example.txt', 'Hello World!')
   */
  async writeFile(path: string, content: string | ArrayBuffer) {
    this.#fs.set(path, content)
  }

  /**
   * @example
   * await fs.writeFile('example.json', { message: 'Hello World!' })
   */
  async writeJSONFile(path: string, content: Parameters<typeof JSON.stringify>[0]) {
    this.writeFile(path, JSON.stringify(content))
  }

  /**
   * @example
   * const file = await fs.readFile('example.txt')
   */
  async readFile(path: string): Promise<File | undefined> {
    const filename = path.split('/').at(-1)
    const content = this.#fs.get(path)
    if (filename && content) {
      return new File([content], filename)
    }
  }

  /**
   * @example
   * const json = await fs.readJSONFile('example.json')
   */
  async readJSONFile(path: string) {
    const file = await this.readFile(path)

    return file ? JSON.parse(await file.text()) : undefined
  }

  /**
   * Returns all entries within the given directory.
   */
  async readDirectory(path: string) {
    async function* generator(self: InMemoryOPFS) {
      for (const key of self.#fs.keys()) {
        if (key.startsWith(path)) {
          const filename = key.split('/').at(-1)
          const content = await self.readFile(key)

          if (filename && content)
            yield {
              kind: 'file',
              getFile: async () => {
                return new File([content], filename)
              },
            } as unknown as FileSystemFileHandle
        }
      }
    }

    return generator(this)
  }

  /**
   * Removes a directory.
   */
  async removeDirectory(path: string) {
    for (const key of this.#fs.keys()) {
      if (key.startsWith(path)) {
        this.#fs.delete(key)
      }
    }
  }

  dispose() {}
}

let hasStorageAccess = false

/**
 * Interact with the Origin Private File System by providing a root directory.
 * All operations will be relative to the root.
 *
 * @example
 * ```js
 * const opfs = useOPFS(new URL('opfs://example-directory'))
 *
 * await opfs.value.writeFile('example.txt', 'Hello World!')
 * // -> creates a file at path `/example-directory/example.txt`
 * ```
 */
export function useOPFS(root: MaybeRefOrGetter<URL | undefined>) {
  const fs = shallowRef<AbstractOPFS>()

  watch(
    () => toValue(root),
    async (dir, _, onCleanup) => {
      onCleanup(() => {
        fs.value?.dispose()
      })

      try {
        await navigator.storage.getDirectory()
        hasStorageAccess = true
      } catch (error) {
        hasStorageAccess = false
      }

      if (dir) {
        if (hasStorageAccess) {
          fs.value = await OPFS.initialize(dir)
        } else {
          log('Unable to access OPFS.', dir)
          fs.value = await Promise.resolve(new InMemoryOPFS())
        }
      }
    },
    { immediate: true }
  )

  return computed(() => fs.value)
}
